import { ANALYTICS_ONLY_EVENT_TYPES, GUIDANCE_MAX_EVENTS_PER_REQUEST } from '@bookora/shared';

import type { GuidanceStateResponse } from '@/api/generated/model';

import { toWire, type QueuedEvent } from './events';
import { QUEUE_MAX, QUEUE_RETRY_MS, STEP_VIEWED_FLUSH_MS } from './timing';

/**
 * The in-memory outbox for `POST /guidance/events`.
 *
 *  · Fire-and-forget: a component records and moves on; nothing waits on it
 *    and no failure is ever shown (the optimistic fold already happened).
 *  · State events go out at once; analytics-only ones (`STEP_VIEWED`,
 *    `STEP_SKIPPED`, `HUB_OPENED`) ride along with the next flush, or after
 *    ten seconds on their own.
 *  · At most 50 held; on overflow the analytics-only ones go first.
 *  · A failure keeps the batch and retries ONCE after five seconds, then
 *    waits for the next successful guidance request or the browser's
 *    `online`. A reload loses what is unsent — accepted (tour spec §1.12).
 *  · Every event carries the clinic it was built under. After a clinic switch
 *    the old clinic's events are dropped, never sent with the new token.
 *  · Nothing is enqueued while impersonating.
 */

const ANALYTICS_ONLY = new Set<string>(ANALYTICS_ONLY_EVENT_TYPES);

export function isAnalyticsOnly(ev: Pick<QueuedEvent, 'type'>): boolean {
  return ANALYTICS_ONLY.has(ev.type);
}

export interface QueueDeps {
  send(events: Record<string, unknown>[]): Promise<GuidanceStateResponse>;
  activeClinicId(): string | null;
  impersonating(): boolean;
  /** A batch was accepted; `stillQueued` are this clinic's events not yet sent. */
  onResponse(
    clinicId: string,
    response: GuidanceStateResponse,
    stillQueued: readonly QueuedEvent[],
  ): void;
  /** A client error the server will never accept (poison batch): dropped. */
  isPermanentFailure?(error: unknown): boolean;
}

export interface GuidanceQueue {
  enqueue(ev: QueuedEvent): void;
  flush(): Promise<void>;
  /** A guidance request succeeded or the browser came back online. */
  retry(): void;
  /** Drop every event not built under `clinicId`. */
  keepOnly(clinicId: string | null): void;
  pending(): readonly QueuedEvent[];
  configure(deps: QueueDeps): void;
  reset(): void;
}

export function createGuidanceQueue(initial?: QueueDeps): GuidanceQueue {
  let deps: QueueDeps | undefined = initial;
  let queue: QueuedEvent[] = [];
  let inFlight = false;
  let retriedOnce = false;
  let immediateTimer: ReturnType<typeof setTimeout> | null = null;
  let analyticsTimer: ReturnType<typeof setTimeout> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const clear = (timer: ReturnType<typeof setTimeout> | null): null => {
    if (timer !== null) clearTimeout(timer);
    return null;
  };

  function trim(): void {
    while (queue.length > QUEUE_MAX) {
      const analytics = queue.findIndex(isAnalyticsOnly);
      queue.splice(analytics === -1 ? 0 : analytics, 1);
    }
  }

  function scheduleImmediate(): void {
    if (immediateTimer !== null) return;
    immediateTimer = setTimeout(() => {
      immediateTimer = null;
      void api.flush();
    }, 0);
  }

  function scheduleAnalytics(): void {
    if (analyticsTimer !== null) return;
    analyticsTimer = setTimeout(() => {
      analyticsTimer = null;
      void api.flush();
    }, STEP_VIEWED_FLUSH_MS);
  }

  const api: GuidanceQueue = {
    configure(next) {
      deps = next;
      if (queue.length > 0) scheduleImmediate();
    },

    enqueue(ev) {
      // Held until `configure()` if nothing is attached yet.
      if (deps?.impersonating()) return;
      queue.push(ev);
      trim();
      if (isAnalyticsOnly(ev)) scheduleAnalytics();
      else scheduleImmediate();
    },

    async flush() {
      if (!deps || inFlight) return;
      const active = deps.activeClinicId();
      queue = queue.filter((ev) => ev.clinicId === active);
      if (queue.length === 0 || active === null) return;
      if (deps.impersonating()) {
        queue = [];
        return;
      }
      analyticsTimer = clear(analyticsTimer);
      const batch = queue.slice(0, GUIDANCE_MAX_EVENTS_PER_REQUEST);
      inFlight = true;
      try {
        const response = await deps.send(batch.map(toWire));
        const sent = new Set(batch.map((ev) => ev.clientEventId));
        queue = queue.filter((ev) => !sent.has(ev.clientEventId));
        retriedOnce = false;
        retryTimer = clear(retryTimer);
        inFlight = false;
        // The clinic may have changed while the request was in flight; a
        // response for another clinic is not written into this one's cache.
        if (deps.activeClinicId() === active) {
          deps.onResponse(
            active,
            response,
            queue.filter((ev) => ev.clinicId === active),
          );
        }
        if (queue.some((ev) => !isAnalyticsOnly(ev))) scheduleImmediate();
        else if (queue.length > 0) scheduleAnalytics();
      } catch (error) {
        inFlight = false;
        if (deps.isPermanentFailure?.(error)) {
          const failed = new Set(batch.map((ev) => ev.clientEventId));
          queue = queue.filter((ev) => !failed.has(ev.clientEventId));
          return;
        }
        if (!retriedOnce) {
          retriedOnce = true;
          retryTimer = clear(retryTimer);
          retryTimer = setTimeout(() => {
            retryTimer = null;
            void api.flush();
          }, QUEUE_RETRY_MS);
        }
      }
    },

    retry() {
      if (queue.length === 0) return;
      retriedOnce = false;
      void api.flush();
    },

    keepOnly(clinicId) {
      queue = queue.filter((ev) => ev.clinicId === clinicId);
    },

    pending() {
      return queue;
    },

    reset() {
      queue = [];
      inFlight = false;
      retriedOnce = false;
      immediateTimer = clear(immediateTimer);
      analyticsTimer = clear(analyticsTimer);
      retryTimer = clear(retryTimer);
    },
  };
  return api;
}

/** The panel's one queue; `QueueFlusher` (in `GuidanceRoot`) configures it. */
export const guidanceQueue = createGuidanceQueue();
